import { featureCollection } from "@turf/helpers";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";

export function WFStoGeoJson(str: string): { layerName: string, geoJson: any }[] {
   let dom = new DOMParser().parseFromString(removeXmlNsPrefix(str), "text/xml");

   let crs: string | null = null;
   let memberTagnames = [...GetUniqueMemberTagNames(dom)];

   let results = memberTagnames.map((tagname) => {
      let features = XMLGetFeatureMembers(dom, tagname).map((a) => {
         let fContent = a.firstElementChild;
         let geometry: { type: string, coordinates: number[][] | number[][][] | number[] } | null = null;
         let properties = {}
         if (fContent) {
            [...fContent.children].forEach(element => {
               if (element.firstElementChild?.getAttribute("srsName")) {
                  let geomElement = element.firstElementChild;
                  crs = geomElement.getAttribute("srsName") || "";
                  let geom = GeometryToGeojson(geomElement);
                  if (geom) {
                     geometry = geom as any;
                  }
                  return;
               }
               // GML Geometry
               if (isTagnameGeometry(element.tagName)) {
                  let geom = NestedGeometryToGeojson(element);
                  if (geom) {
                     geometry = geom as any;
                  }
                  return;
               }

               if (element.childNodes.length == 1) {
                  let prop = element.tagName;
                  if (prop) {
                     properties = { ...properties, [prop]: element.textContent }
                  }
               } else {
                  // Nested properties
                  let nested = element.tagName
                  let nestedObj: any = {};
                  [...element.children].forEach((child) => {
                     nestedObj[child.tagName] = child.textContent;
                  });
                  properties = { ...properties, [nested]: nestedObj }
               }
            });
            if (!geometry) {
               console.warn("No geometry found for feature:", fContent);
            }
            return {
               type: "Feature",
               id: "",
               geometry: geometry,
               properties: properties
            }
         }
      })
      let FC = featureCollection(features as any);
      if (crs) {
         FC["crs"] = crs;
      } else {
         FC["crs"] = "EPSG::25832"; // TODO: find a better way to handle missing crs
      }
      let metadata = parseMetadata(dom);
      if (metadata) {
         FC["metadata"] = metadata;
      }
      return { layerName: parseLayerName(tagname), geoJson: transFormToLnglat(FC) };
   })
   if (results.length === 0) {
      return [
         { layerName: "default", geoJson: { ...featureCollection([]), metadata: parseMetadata(dom) } }
      ]
   }

   return results;
}

function transFormToLnglat(geoJson: any) {
   if ("crs" in geoJson && geoJson.crs) {
      let crs = (geoJson.crs as any).split("EPSG::").at(-1);
      if (crs && crs != "4326") {
         geoJson = GenerateGeomUtils.geoJsonCoordsTransformer(geoJson, (coord) => {
            let latLng = GenerateGeomUtils.convertFromUTMtoLatLng2(coord.reverse() as [number, number]).toReversed();
            return latLng as [number, number];
         });
      }
      delete (geoJson as any).crs;
   }
   return geoJson;
}

function GeometryToGeojson(elem: Element) {
   let srsDimension = findSRSDimension(elem);
   let type = elem.tagName
   switch (type) {
      case "Polygon":
         return PolygonToGeojson(elem, srsDimension ?? 2);
      case "Point":
         return PointToGeojson(elem, srsDimension ?? 2);
      case "LineString":
         return LineStringToGeojson(elem, srsDimension ?? 2);
      case "MultiSurface":
         return SurfaceToGeojson(elem, srsDimension ?? 2);
      case "MultiCurve":
         return CurveToGeojson(elem, srsDimension ?? 2);
   }
}

function NestedGeometryToGeojson(elem: Element) {
   if (elem.firstElementChild)
      return GeometryToGeojson(elem.firstElementChild)
   return null;
}

let geomTypes = ["curveProperty", "surfaceProperty", "pointProperty", "polygon", "lineString", "point", "curve", "surface", "MultiCurve", "MultiSurface"];
function isTagnameGeometry(tagName: string): boolean {
   if (geomTypes.find((type) => tagName.endsWith(type))) {
      return true;
   }
   return false;
}

function CurveToGeojson(elem: Element, srsDimension: number) {
   let name = elem.tagName;
   if (name.endsWith("Curve")) {
      let lineStrings = [...elem.getElementsByTagName("LineString")].map((lineString) => {
         return LineStringToGeojson(lineString, srsDimension);
      });
      return {
         type: "MultiLineString",
         coordinates: lineStrings.filter((a) => a).map(p => p!.coordinates as number[][])
      };
   }
}

function SurfaceToGeojson(elem: Element, srsDimension: number) {
   let name = elem.tagName;
   if (name.endsWith("Surface")) {
      let polygons = [...elem.getElementsByTagName("Polygon")].map((polygon) => {
         return PolygonToGeojson(polygon, srsDimension);
      });
      return {
         type: "MultiPolygon",
         coordinates: polygons.filter((a) => a).map(p => p!.coordinates as number[][][])
      }

   }
}

function PolygonToGeojson(elem: Element, srsDimension: number) {
   let name = elem.tagName;
   if (name.endsWith("Polygon")) {
      let posList = [...elem.getElementsByTagName("posList")].map((a) => {
         let content = a.textContent;
         return parseXMLCoords(content || "", srsDimension);
      })
      return {
         type: "Polygon",
         coordinates: posList
      }
   }
}

function PointToGeojson(elem: Element, srsDimension: number) {
   let name = elem.tagName;
   if (name.endsWith("Point")) {
      let coordinates = elem.getElementsByTagName("pos")[0];
      let content = coordinates.textContent;
      let coords = parseXMLCoords(content || "", srsDimension)[0];
      return {
         type: "Point",
         coordinates: coords
      }
   }
}



function LineStringToGeojson(elem: Element, srsDimension: number) {
   let name = elem.tagName;
   if (name.endsWith("LineString")) {
      let posList = elem.getElementsByTagName("posList")[0];
      let content = posList.textContent;
      return {
         type: "LineString",
         coordinates: parseXMLCoords(content || "", srsDimension)
      }
   }
}

function parseXMLCoords(str: string, dimension: number): number[][] {
   let flat = str.replaceAll(/^\D*/gm, "").replaceAll(/\D*$/gm, "").replaceAll("  ", " ").split(" ");
   const dimNPair = (idx: number, dim: number) => {
      let path = idx * dim;
      return flat.slice(path, path + dim).map(v => parseFloat(v)).reverse().slice(dim - 2) as number[];
   };
   return new Array(Math.floor(flat.length / dimension)).fill(0).map((a, idx) => dimNPair(idx, dimension));
}

function removeXmlNsPrefix(xmlString: string): string {
   return xmlString
      .replace(/(<\/?)(\S*?:)?/g, '$1')
}

function parseMetadata(elem: Element | Document) {
   let metaElem = elem.getElementsByTagName("FeatureCollection")[0];
   if (!metaElem) return null;
   return {
      numberMatched: metaElem.getAttribute("numberMatched"),
      numberReturned: metaElem.getAttribute("numberReturned"),
      timeStamp: metaElem.getAttribute("timeStamp"),
   }
}

function GetUniqueMemberTagNames(elem: Element | Document): Set<string> {
   let tagnames = new Set<string>();
   if (elem.getElementsByTagName("featureMember").length > 0) {
      tagnames.add("featureMember");
   }
   if (elem.getElementsByTagName("member").length > 0) {
      tagnames.add("member");
   }
   let extraTagNames = findFirstMatches(elem.children, /Member$/);
   extraTagNames.forEach(tagName => tagnames.add(tagName));
   return tagnames;
}


function XMLGetFeatureMembers(elem: Element | Document, tagname: string): Element[] {
   return [...elem.getElementsByTagName(tagname)];
}

function findFirstMatches(list: HTMLCollection, find: RegExp, depth = 3): Set<string> {
   let tagNames = new Set<string>();
   if (depth <= 0) return tagNames;
   let listArray = Array.from(list);
   for (let i = 0; i < listArray.length; i++) {
      if (find.test(listArray[i].tagName)) {
         tagNames.add(listArray[i].tagName);
      }
   }
   if (tagNames.size > 0) {
      return tagNames;
   }
   for (let i = 0; i < listArray.length; i++) {
      let res = findFirstMatches(listArray[i].children, find, depth - 1);
      if (res.size > 0) return res;
   }
   return tagNames;
}

function parseLayerName(tagname: string): string {
   return tagname.replace(/Member$/, "") || "";
}

function findSRSDimension(elem: Element): number | null {
   let srsDimension: number | null = null;
   if (elem.getAttribute("srsDimension")) {
      return parseInt(elem.getAttribute("srsDimension") || "2");
   }
   for (let i of elem.children) {
      srsDimension = findSRSDimension(i);
      if (srsDimension) {
         return srsDimension;
      }
   }
   return null;
}

export function xmlExceptionCheck(str: string): string | null {
   let dom = new DOMParser().parseFromString(removeXmlNsPrefix(str), "text/xml");
   let exceptionElem = dom.querySelector("ExceptionText");
   let exceptionText = exceptionElem?.textContent
   return exceptionText || null;
}